// In-game HUD do dostosowania rozmiaru i pozycji ubrań Natalki.
import { TWEAK_LIMITS } from './clothDefaults.js';
// Klawisz T toggle, lub klikanie ikony 👗 w prawym górnym rogu.
// Zapis na serwer (stan gracza) przez ClothTweaks — konfiguracja jest per-konto, nie per-urządzenie.

import Phaser from 'phaser';
import { GAME_WIDTH } from '../config.js';
import { iconText } from './iconText.js';
import { pinToCamera } from './pin.mjs';

const LAYERS = ['top', 'bottom', 'shoes', 'accessory'];
const LAYER_LABELS = { top: 'Bluzka', bottom: 'Spódnica', shoes: 'Buty', accessory: 'Akcesorium' };

export class ClothTweakHud {
  constructor(scene, natalka) {
    this.scene = scene;
    this.natalka = natalka;
    this.currentLayer = 'top';
    this.container = null;
    this.triggerBtn = null;

    this._keyHandler = () => this.toggle();
    scene.input.keyboard?.on('keydown-T', this._keyHandler);
    scene.events.once('shutdown', () => this._cleanup());
  }

  // Doda ikonę 👗 w prawym górnym rogu otwierającą HUD
  addTriggerButton() {
    if (this.triggerBtn) return;
    const X = 55;
    const Y = 90;
    const tex = this.scene.textures.exists('cloth_spodniczka_pink') ? 'cloth_spodniczka_pink' : null;

    const bg = this.scene.add.circle(X, Y, 32, 0xffffff, 0.95)
      .setStrokeStyle(2, 0xffd166).setScrollFactor(0).setDepth(9000)
      .setInteractive({ useHandCursor: true });
    let icon;
    if (tex) {
      icon = this.scene.add.image(X, Y, tex).setDisplaySize(44, 44).setScrollFactor(0).setDepth(9001);
    } else {
      icon = iconText(this.scene, X, Y, '{dress}', { fontSize: '32px' }).setOrigin(0.5).setScrollFactor(0).setDepth(9001);
    }
    bg.on('pointerup', () => this.toggle());
    this.triggerBtn = { bg, icon };
  }

  _cleanup() {
    this.scene.input.keyboard?.off('keydown-T', this._keyHandler);
    this.container?.destroy(true);
    this.container = null;
    this.triggerBtn?.bg.destroy();
    this.triggerBtn?.icon.destroy();
    this.triggerBtn = null;
    // Phaser reuses the scene instance across restarts — clear the cached ref so the
    // next entry's NatalkaSprite rebuilds the HUD + 👗 trigger button (instead of the
    // guard seeing a stale reference and skipping it).
    this.scene._clothTweakHud = null;
  }

  toggle() {
    if (this.container) this.hide();
    else this.show();
  }

  show() {
    if (this.container) return;
    this.container = this.scene.add.container(0, 0).setDepth(10000).setScrollFactor(0);
    this._render();
  }

  hide() {
    this.container?.destroy(true);
    this.container = null;
  }

  _render() {
    if (!this.container) return;
    this.container.removeAll(true);

    const W = 380, H = 420;
    const X = GAME_WIDTH - W - 20, Y = 130;

    const bg = this.scene.add.rectangle(X, Y, W, H, 0x000000, 0.9).setOrigin(0, 0).setStrokeStyle(2, 0xffd166);
    this.container.add(bg);

    const title = iconText(this.scene, X + 10, Y + 8, '{palette} Dostosuj ubranko', {
      fontFamily: 'system-ui', fontSize: '16px', color: '#ffd166', fontStyle: 'bold',
    });
    this.container.add(title);

    const closeBtn = this._mkBtn(X + W - 36, Y + 8, 28, 24, '{cross}', () => this.hide(), 0xaa3333);

    // Wybór warstwy — 4 przyciski
    LAYERS.forEach((layer, i) => {
      const isActive = layer === this.currentLayer;
      this._mkBtn(X + 10 + i * 90, Y + 40, 85, 28, LAYER_LABELS[layer], () => {
        this.currentLayer = layer;
        this._render();
      }, isActive ? 0xffd166 : 0x444444, isActive ? 0x000000 : 0xffffff);
    });

    const info = this.natalka.getLayerInfo(this.currentLayer);
    if (!info.key) {
      const note = iconText(this.scene, X + 10, Y + 90, '(nic nie założone w tej kategorii)', {
        fontFamily: 'system-ui', fontSize: '13px', color: '#aaa',
      });
      this.container.add(note);
      return;
    }

    const tw = info.tweak;
    // Sliders: [label, prop, current_value, min, max, format]
    const rows = [
      { label: '{arrows_lr} Szerokość', prop: 'scaleW',  val: tw.scaleW,  min: TWEAK_LIMITS.scaleMin, max: TWEAK_LIMITS.scaleMax, fmt: v => `${Math.round(v * 100)}%` },
      { label: '{arrows_ud} Wysokość',  prop: 'scaleH',  val: tw.scaleH,  min: TWEAK_LIMITS.scaleMin, max: TWEAK_LIMITS.scaleMax, fmt: v => `${Math.round(v * 100)}%` },
      { label: '{arrow_left} Lewo / Prawo {arrow_right}', prop: 'offsetX', val: tw.offsetX, min: TWEAK_LIMITS.offsetMin, max: TWEAK_LIMITS.offsetMax, fmt: v => `${Math.round(v)}` },
      { label: '{arrow_up} Góra / Dół {arrow_down}',  prop: 'offsetY', val: tw.offsetY, min: TWEAK_LIMITS.offsetMin, max: TWEAK_LIMITS.offsetMax, fmt: v => `${Math.round(v)}` },
    ];

    rows.forEach((row, i) => {
      const rowY = Y + 95 + i * 65;

      const labelText = iconText(this.scene, X + 10, rowY, row.label, {
        fontFamily: 'system-ui', fontSize: '14px', color: '#fff',
      });
      this.container.add(labelText);

      const valText = iconText(this.scene, X + W - 60, rowY, row.fmt(row.val), {
        fontFamily: 'system-ui', fontSize: '14px', color: '#ffd166', fontStyle: 'bold',
      });
      this.container.add(valText);

      this._mkSlider(X + 10, rowY + 30, W - 30, row.min, row.max, row.val, (newVal) => {
        this.natalka.applyLayerTweak(this.currentLayer, { [row.prop]: newVal });
        valText.setText(row.fmt(newVal));
      });
    });

    // Reset
    this._mkBtn(X + 10, Y + 370, 120, 36, '{repeat} Resetuj', () => {
      this.natalka.resetLayerTweak(this.currentLayer);
      this._render();
    }, 0xaa3333);
    pinToCamera(this.container);   // setScrollFactor(0) on the container alone leaves the children hit-testing in world space
  }

  _mkSlider(x, y, w, min, max, val, onChange) {
    const trackH = 10;
    const handleW = 22;
    const handleH = 32;

    const track = this.scene.add.rectangle(x, y, w, trackH, 0x333333, 1)
      .setOrigin(0, 0.5).setStrokeStyle(1, 0x000000);
    const t0 = Phaser.Math.Clamp((val - min) / (max - min), 0, 1);
    const fill = this.scene.add.rectangle(x, y, w * t0, trackH, 0xffd166, 1).setOrigin(0, 0.5);
    const handle = this.scene.add.rectangle(x + w * t0, y, handleW, handleH, 0xffffff, 1)
      .setStrokeStyle(2, 0x000000).setInteractive({ draggable: true, useHandCursor: true });
    this.scene.input.setDraggable(handle);

    const updateFromX = (worldX) => {
      const clamped = Phaser.Math.Clamp(worldX, x, x + w);
      handle.x = clamped;
      fill.width = clamped - x;
      const t = (clamped - x) / w;
      const newVal = min + t * (max - min);
      onChange(newVal);
    };

    handle.on('drag', (_p, dragX) => updateFromX(dragX));

    // The panel is pinned to the screen: a world x minus the scroll is its x (the street and Podwórko scroll).
    track.setInteractive({ useHandCursor: true }).on('pointerdown', (pointer) => updateFromX(pointer.worldX - this.scene.cameras.main.scrollX));

    this.container.add(track);
    this.container.add(fill);
    this.container.add(handle);
  }

  _mkBtn(x, y, w, h, label, fn, fill = 0x444444, textColor = 0xffffff) {
    const rect = this.scene.add.rectangle(x, y, w, h, fill, 0.95)
      .setOrigin(0, 0).setStrokeStyle(1, 0xffd166).setInteractive({ useHandCursor: true });
    rect.on('pointerup', fn);
    const text = iconText(this.scene, x + w / 2, y + h / 2, label, {
      fontFamily: 'system-ui', fontSize: '13px',
      color: '#' + textColor.toString(16).padStart(6, '0'),
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.container.add(rect);
    this.container.add(text);
    return rect;
  }
}
